import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import LabelFormatCanvas from '../components/LabelFormatCanvas.jsx';
import Modal from '../components/Modal.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';

const layoutModeOptions = [
  ['CONTINUOUS', 'Contínua'],
  ['PAGE', 'Página'],
];

const mediaTypeOptions = [
  ['GAP', 'Gap'],
  ['BLACK_MARK', 'Marca preta'],
  ['CONTINUOUS', 'Contínua'],
  ['FANFOLD', 'Formulário'],
  ['DIE_CUT', 'Pré-cortada'],
  ['TAG', 'Tag'],
  ['RIBBON', 'Ribbon'],
  ['RFID', 'RFID'],
];

const stockTypeOptions = [
  ['NONE', 'Nenhum'],
  ['LABEL', 'Etiqueta'],
  ['TAG', 'Tag'],
  ['RFID', 'RFID'],
  ['FORM', 'Formulário'],
];

const previewOrderOptions = [
  ['LEFT_TO_RIGHT', 'Esquerda → direita'],
  ['TOP_TO_BOTTOM', 'Cima → baixo'],
  ['COLUMN_BY_COLUMN', 'Coluna → coluna'],
  ['ROW_BY_ROW', 'Linha → linha'],
];

const statusOptions = [
  ['ACTIVE', 'Ativo'],
  ['ARCHIVED', 'Arquivado'],
];

const blankForm = () => ({
  name: '',
  description: '',
  layoutMode: 'CONTINUOUS',
  mediaType: 'GAP',
  stockType: 'LABEL',
  manufacturer: '',
  model: '',
  paperWidthMm: 110,
  paperHeightMm: 50,
  columnWidthMm: 100,
  columnHeightMm: 50,
  widthMm: 100,
  heightMm: 50,
  leftMarginMm: 0,
  topMarginMm: 0,
  rightMarginMm: 0,
  bottomMarginMm: 0,
  pageWidthMm: 110,
  pageHeightMm: 160,
  columns: 1,
  rows: 4,
  previewRows: 4,
  horizontalGapMm: 0,
  verticalGapMm: 0,
  previewOrder: 'ROW_BY_ROW',
  status: 'ACTIVE',
});

function toForm(data) {
  const base = blankForm();
  return {
    ...base,
    ...data,
    paperWidthMm: data.paperWidthMm ?? data.pageWidthMm ?? base.paperWidthMm,
    paperHeightMm: data.paperHeightMm ?? data.pageHeightMm ?? base.paperHeightMm,
    columnWidthMm: data.columnWidthMm ?? data.widthMm ?? base.columnWidthMm,
    columnHeightMm: data.columnHeightMm ?? data.heightMm ?? base.columnHeightMm,
    widthMm: data.columnWidthMm ?? data.widthMm ?? base.widthMm,
    heightMm: data.columnHeightMm ?? data.heightMm ?? base.heightMm,
    pageWidthMm: data.pageWidthMm ?? base.pageWidthMm,
    pageHeightMm: data.pageHeightMm ?? base.pageHeightMm,
    rows: data.rows ?? base.rows,
    previewRows: data.previewRows ?? base.previewRows,
  };
}

function NumberInput({ label, value, step = '0.1', onChange }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold text-on-surface-variant">{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
      />
    </label>
  );
}

function SelectInput({ label, value, options, onChange }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold text-on-surface-variant">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function formatDimensions(item) {
  return `${item.columnWidthMm || item.widthMm} x ${item.columnHeightMm || item.heightMm} mm por coluna/etiqueta • ${item.columns} coluna(s)${item.rows ? ` • ${item.rows} linha(s)` : ''}`;
}

function formatMedia(item) {
  return item.layoutMode === 'PAGE'
    ? `${item.paperWidthMm || item.pageWidthMm || '-'} x ${item.paperHeightMm || item.pageHeightMm || '-'} mm por página`
    : `${item.paperWidthMm || item.pageWidthMm || ((item.columnWidthMm || item.widthMm) * item.columns)} x ${item.paperHeightMm || item.pageHeightMm || (item.columnHeightMm || item.heightMm)} mm de mídia`;
}

export default function LabelFormats({ user }) {
  const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';
  const fileInputRef = useRef(null);
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(blankForm());
  const [isFormActive, setIsFormActive] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('card');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  async function loadList(preserveSelection = true, preferredSelectionId = '') {
    try {
      const items = await api.listLabelFormats();
      setList(items);
      if (preferredSelectionId && items.some((item) => item.id === preferredSelectionId)) {
        setSelectedId(preferredSelectionId);
        setIsFormActive(true);
        return;
      }
      if (preserveSelection && selectedId && items.some((item) => item.id === selectedId)) {
        return;
      }
      setSelectedId('');
      setIsFormActive(false);
    } catch (e) {
      setError(e.message);
    }
  }

  async function loadOne(id) {
    if (!id) {
      setForm(blankForm());
      return;
    }
    try {
      const detail = await api.getLabelFormat(id);
      setForm(toForm(detail));
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    loadList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFormActive) loadOne(selectedId);
  }, [isFormActive, selectedId]);

  function updateField(field, value) {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'columnWidthMm') next.widthMm = value;
      if (field === 'columnHeightMm') next.heightMm = value;
      if (field === 'widthMm') next.columnWidthMm = value;
      if (field === 'heightMm') next.columnHeightMm = value;
      return next;
    });
  }

  function buildPayload() {
    return {
      name: form.name,
      description: form.description || undefined,
      layoutMode: form.layoutMode,
      mediaType: form.mediaType,
      stockType: form.stockType,
      manufacturer: form.manufacturer || undefined,
      model: form.model || undefined,
      paperWidthMm: form.paperWidthMm,
      paperHeightMm: form.paperHeightMm,
      columnWidthMm: form.columnWidthMm,
      columnHeightMm: form.columnHeightMm,
      widthMm: form.columnWidthMm,
      heightMm: form.columnHeightMm,
      leftMarginMm: form.leftMarginMm,
      topMarginMm: form.topMarginMm,
      rightMarginMm: form.rightMarginMm,
      bottomMarginMm: form.bottomMarginMm,
      pageWidthMm: form.paperWidthMm,
      pageHeightMm: form.paperHeightMm,
      columns: form.columns,
      rows: form.layoutMode === 'PAGE' ? form.rows : undefined,
      previewRows: form.previewRows,
      horizontalGapMm: form.horizontalGapMm,
      verticalGapMm: form.verticalGapMm,
      previewOrder: form.previewOrder,
      status: form.status,
    };
  }

  const previewModel = {
    layoutMode: form.layoutMode,
    widthMm: form.columnWidthMm,
    heightMm: form.columnHeightMm,
    paperWidthMm: form.paperWidthMm,
    paperHeightMm: form.paperHeightMm,
    pageWidthMm: form.paperWidthMm,
    pageHeightMm: form.paperHeightMm,
    columns: form.columns,
    rows: form.layoutMode === 'PAGE' ? form.rows : undefined,
    previewRows: form.previewRows,
    payload: {
      layoutMode: form.layoutMode,
      widthMm: form.columnWidthMm,
      heightMm: form.columnHeightMm,
      paperWidthMm: form.paperWidthMm,
      paperHeightMm: form.paperHeightMm,
      leftMarginMm: form.leftMarginMm,
      topMarginMm: form.topMarginMm,
      rightMarginMm: form.rightMarginMm,
      bottomMarginMm: form.bottomMarginMm,
      pageWidthMm: form.paperWidthMm,
      pageHeightMm: form.paperHeightMm,
      columns: form.columns,
      rows: form.layoutMode === 'PAGE' ? form.rows : undefined,
      previewRows: form.previewRows,
      horizontalGapMm: form.horizontalGapMm,
      verticalGapMm: form.verticalGapMm,
      previewOrder: form.previewOrder,
      mediaType: form.mediaType,
    },
  };

  function openNew() {
    setSelectedId('');
    setForm(blankForm());
    setIsFormActive(true);
    setStatus('Novo formato pronto para preenchimento.');
  }

  function openEdit(item) {
    setSelectedId(item.id);
    setIsFormActive(true);
  }

  function closeEditor() {
    setIsFormActive(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    try {
      const payload = buildPayload();
      let nextSelectedId = selectedId;
      if (selectedId) {
        await api.updateLabelFormat(selectedId, payload);
        setStatus('Formato atualizado.');
      } else {
        const created = await api.createLabelFormat(payload);
        setSelectedId(created.id);
        nextSelectedId = created.id;
        setStatus('Formato criado.');
      }
      await loadList(true, nextSelectedId);
      if (nextSelectedId) {
        await loadOne(nextSelectedId);
      }
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleRemove() {
    if (!deleteTarget) return;
    setError('');
    setStatus('');
    try {
      await api.deleteLabelFormat(deleteTarget.id);
      setStatus('Formato removido.');
      setDeleteTarget(null);
      closeEditor();
      setSelectedId('');
      await loadList(false);
    } catch (e) {
      setError(e.message);
      setDeleteTarget(null);
    }
  }

  async function handleImportFile(file) {
    if (!file) return;
    try {
      const raw = JSON.parse(await file.text());
      applyImportedFormat(raw, `Arquivo "${file.name}" carregado. Revise e salve.`);
    } catch {
      setError('Não foi possível importar o arquivo do formato.');
    }
  }

  function applyImportedFormat(raw, successMessage) {
    const payload = toForm({
      ...blankForm(),
      ...raw,
    });
    setForm(payload);
    setIsFormActive(true);
    setSelectedId('');
    setIsImportModalOpen(false);
    setImportJsonText('');
    setStatus(successMessage);
    setError('');
  }

  function handleImportFromText() {
    try {
      const raw = JSON.parse(importJsonText);
      applyImportedFormat(raw, 'JSON colado carregado. Revise e salve.');
    } catch {
      setError('O JSON colado é inválido.');
    }
  }

  const normalizedSearch = search.trim().toLowerCase();
  const filteredList = list.filter((item) => item.name.toLowerCase().includes(normalizedSearch));

  const viewSwitch = (
    <div className="flex items-center gap-1 rounded-2xl border border-surface-container-high bg-surface-container-low p-1">
      <button
        type="button"
        title="Visualizar como cards"
        onClick={() => setView('card')}
        className={`grid h-7 w-9 place-items-center rounded-xl transition ${view === 'card' ? 'bg-primary text-on-primary shadow-[0_8px_16px_rgba(249,115,22,0.25)]' : 'text-on-surface-variant hover:text-on-surface'}`}
      >
        <span className="material-symbols-outlined text-[15px]">grid_view</span>
      </button>
      <button
        type="button"
        title="Visualizar como tabela"
        onClick={() => setView('table')}
        className={`grid h-7 w-9 place-items-center rounded-xl transition ${view === 'table' ? 'bg-primary text-on-primary shadow-[0_8px_16px_rgba(249,115,22,0.25)]' : 'text-on-surface-variant hover:text-on-surface'}`}
      >
        <span className="material-symbols-outlined text-[15px]">view_list</span>
      </button>
    </div>
  );

  const deleteButton = (item) =>
    canManage && (
      <button
        type="button"
        title="Excluir"
        onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
        className="grid h-7 w-7 place-items-center rounded-lg border border-error/20 bg-error-container/40 text-error transition hover:bg-error-container/70"
      >
        <span className="material-symbols-outlined text-[13px]">delete</span>
      </button>
    );

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-[28px] border border-surface-container-highest bg-surface px-5 py-5 shadow-[0_14px_28px_rgba(15,23,42,0.05)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">Estúdio de formatos</div>
            <h1 className="studio-uniform-title mt-2 text-on-surface">Formatos de etiqueta</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {viewSwitch}
            {canManage && (
              <button
                type="button"
                onClick={openNew}
                className="rounded-2xl bg-primary px-4 py-3 text-[10px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]"
              >
                Novo
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/modelos"
            className="rounded-2xl border border-surface-container-high px-4 py-2.5 text-[10px] font-medium text-on-surface"
          >
            Modelos
          </Link>
          <button
            type="button"
            onClick={() => {
              setError('');
              setStatus('');
              setIsImportModalOpen(true);
            }}
            className="rounded-2xl border border-surface-container-high px-4 py-2.5 text-[10px] font-medium text-on-surface"
          >
            Importar JSON
          </button>
          <button
            type="button"
            onClick={() => downloadJson(`${(form.name || 'formato').replace(/\s+/g, '-').toLowerCase()}.label-format.json`, buildPayload())}
            className="rounded-2xl border border-surface-container-high px-4 py-2.5 text-[10px] font-medium text-on-surface"
          >
            Exportar JSON
          </button>
          <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={(e) => handleImportFile(e.target.files?.[0])} />
          <div className="ml-auto w-full sm:w-auto sm:min-w-[260px]">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar formatos..."
              className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
          </div>
        </div>

        <div className="mt-5">
          {list.length === 0 ? (
            <div className="rounded-3xl bg-surface-container-low px-4 py-4 text-[10px] text-on-surface-variant">
              <div>Nenhum formato cadastrado ainda.</div>
              <Link to="/modelos" className="mt-3 inline-flex font-semibold text-primary">
                Criar a partir de um modelo global
              </Link>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="rounded-3xl bg-surface-container-low px-4 py-4 text-[10px] text-on-surface-variant">
              Nenhum formato corresponde à pesquisa.
            </div>
          ) : view === 'card' ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {filteredList.map((item) => (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEdit(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openEdit(item);
                    }
                  }}
                  className={`flex cursor-pointer flex-col rounded-[24px] border px-4 py-4 text-left transition ${selectedId === item.id
                    ? 'border-primary bg-primary-container/50'
                    : 'border-surface-container-high bg-surface-container-low hover:bg-surface-container'
                    }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-semibold text-on-surface">{item.name}</div>
                      <div className="mt-1 text-[10px] text-on-surface-variant">{formatDimensions(item)}</div>
                      <div className="mt-1 text-[10px] text-on-surface-variant">{formatMedia(item)} • {item.mediaType}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="rounded-full bg-surface px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
                        {item.status === 'ACTIVE' ? 'Ativo' : 'Arquivado'}
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          title="Excluir"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: item.id, name: item.name });
                          }}
                          className="grid h-7 w-7 place-items-center rounded-lg border border-error/20 bg-error-container/40 text-error transition hover:bg-error-container/70"
                        >
                          <span className="material-symbols-outlined text-[13px]">delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-[24px] border border-surface-container-high">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-surface-container-high">
                    <th className="px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Formato</th>
                    <th className="px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Dimensões</th>
                    <th className="px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Mídia</th>
                    <th className="px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Status</th>
                    <th className="px-4 py-3 text-right text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((item) => (
                    <tr
                      key={item.id}
                      className="cursor-pointer border-b border-surface-container-low transition last:border-0 hover:bg-surface-container-low"
                      onClick={() => openEdit(item)}
                    >
                      <td className="px-4 py-3 text-[11px] font-semibold text-on-surface">{item.name}</td>
                      <td className="px-4 py-3 text-[10px] text-on-surface-variant">{formatDimensions(item)}</td>
                      <td className="px-4 py-3 text-[10px] text-on-surface-variant">{formatMedia(item)} • {item.mediaType}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-surface px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
                          {item.status === 'ACTIVE' ? 'Ativo' : 'Arquivado'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Editar"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(item);
                            }}
                            className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-primary-container/20"
                          >
                            <span className="material-symbols-outlined text-[13px]">edit_square</span>
                          </button>
                          {deleteButton(item)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {error && <div className="rounded-2xl border border-error/20 bg-error-container/40 px-4 py-3 text-[10px] text-error">{error}</div>}
      {status && <div className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[10px] text-on-primary-container">{status}</div>}

      <Modal
        open={isFormActive}
        onClose={closeEditor}
        eyebrow="Estúdio de formatos"
        title={selectedId ? 'Editar formato de etiqueta' : 'Novo formato de etiqueta'}
        maxWidth="max-w-4xl"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <section className="rounded-[28px] border border-surface-container-highest px-6 py-6">
            <div className="mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">Identificação</div>
              <h2 className="studio-uniform-title mt-2 text-on-surface">Descrição da etiqueta</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-2 md:col-span-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Nome</span>
                <input value={form.name} onChange={(e) => updateField('name', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <label className="flex flex-col gap-2 md:col-span-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Descrição</span>
                <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} rows={3} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Fabricante</span>
                <input value={form.manufacturer} onChange={(e) => updateField('manufacturer', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Modelo</span>
                <input value={form.model} onChange={(e) => updateField('model', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
            </div>
          </section>

          <section className="rounded-[28px] border border-surface-container-highest px-6 py-6">
            <div className="mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">Mídia</div>
              <h2 className="studio-uniform-title mt-2 text-on-surface">Formato da etiqueta</h2>
            </div>
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="grid gap-4 md:grid-cols-3">
                <SelectInput label="Modo da superfície" value={form.layoutMode} options={layoutModeOptions} onChange={(value) => updateField('layoutMode', value)} />
                <SelectInput label="Tipo de mídia" value={form.mediaType} options={mediaTypeOptions} onChange={(value) => updateField('mediaType', value)} />
                <SelectInput label="Tipo de estoque" value={form.stockType} options={stockTypeOptions} onChange={(value) => updateField('stockType', value)} />

                <NumberInput label="Largura do papel/mídia (mm)" value={form.paperWidthMm} onChange={(value) => updateField('paperWidthMm', value)} />
                <NumberInput label={form.layoutMode === 'PAGE' ? 'Altura da página/papel (mm)' : 'Altura do passo/picote (mm)'} value={form.paperHeightMm} onChange={(value) => updateField('paperHeightMm', value)} />
                <NumberInput label="Colunas por faixa/página" step="1" value={form.columns} onChange={(value) => updateField('columns', value)} />

                {form.layoutMode === 'PAGE' ? (
                  <>
                    <NumberInput label="Largura de cada coluna/etiqueta (mm)" value={form.columnWidthMm} onChange={(value) => updateField('columnWidthMm', value)} />
                    <NumberInput label="Altura de cada coluna/etiqueta (mm)" value={form.columnHeightMm} onChange={(value) => updateField('columnHeightMm', value)} />
                    <NumberInput label="Linhas por página" step="1" value={form.rows} onChange={(value) => updateField('rows', value)} />
                    <NumberInput label="Linhas de preview" step="1" value={form.previewRows} onChange={(value) => updateField('previewRows', value)} />
                  </>
                ) : (
                  <>
                    <NumberInput label="Largura de cada coluna/etiqueta (mm)" value={form.columnWidthMm} onChange={(value) => updateField('columnWidthMm', value)} />
                    <NumberInput label="Altura de cada coluna/etiqueta (mm)" value={form.columnHeightMm} onChange={(value) => updateField('columnHeightMm', value)} />
                    <NumberInput label="Linhas visíveis na prévia" step="1" value={form.previewRows} onChange={(value) => updateField('previewRows', value)} />
                  </>
                )}

                <NumberInput label="Margem esquerda (mm)" value={form.leftMarginMm} onChange={(value) => updateField('leftMarginMm', value)} />
                <NumberInput label="Margem superior (mm)" value={form.topMarginMm} onChange={(value) => updateField('topMarginMm', value)} />
                <NumberInput label="Margem direita (mm)" value={form.rightMarginMm} onChange={(value) => updateField('rightMarginMm', value)} />
                <NumberInput label="Margem inferior (mm)" value={form.bottomMarginMm} onChange={(value) => updateField('bottomMarginMm', value)} />
                <NumberInput label="Separação horizontal (mm)" value={form.horizontalGapMm} onChange={(value) => updateField('horizontalGapMm', value)} />
                <NumberInput label="Separação vertical (mm)" value={form.verticalGapMm} onChange={(value) => updateField('verticalGapMm', value)} />
                <SelectInput label="Ordem de preenchimento" value={form.previewOrder} options={previewOrderOptions} onChange={(value) => updateField('previewOrder', value)} />
                <SelectInput label="Status" value={form.status} options={statusOptions} onChange={(value) => updateField('status', value)} />
              </div>

              <div className="rounded-[24px] border border-surface-container-high bg-surface-container-low p-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Prévia de impressão</div>
                <LabelFormatCanvas model={previewModel} />
              </div>
            </div>
          </section>

          {canManage && (
            <div className="flex flex-wrap justify-between gap-3">
              <div className="flex gap-3">
                <button type="submit" className="rounded-2xl bg-primary px-5 py-3 text-[11px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]">
                  {selectedId ? 'Salvar formato' : 'Criar formato'}
                </button>
                {selectedId && (
                  <button
                    type="button"
                    onClick={() => setDeleteTarget({ id: selectedId, name: form.name })}
                    className="rounded-2xl border border-error/20 bg-error-container/50 px-5 py-3 text-[11px] font-semibold text-error"
                  >
                    Excluir
                  </button>
                )}
              </div>
              <div className="rounded-2xl bg-surface-container-low px-4 py-3 text-[10px] leading-5 text-on-surface-variant">
                Este cadastro representa o papel: dimensões, layout e mídia. Hardware, DPI e RFID ficam na impressora; segurança fica na etiqueta.
              </div>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Remover formato "${deleteTarget?.name}"?`}
        message="Esta ação remove o formato do banco de dados e fica registrada na auditoria de exclusões."
        confirmLabel="Remover"
        onConfirm={handleRemove}
        onCancel={() => setDeleteTarget(null)}
      />

      <Modal
        open={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportJsonText('');
        }}
        eyebrow="Importação"
        title="Importar formato JSON"
        maxWidth="max-w-4xl"
      >
        <p className="mb-5 text-[11px] leading-6 text-on-surface-variant">
          Você pode importar um arquivo `.json` ou colar o conteúdo bruto na mesma tela.
        </p>
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-[24px] border border-surface-container-high bg-surface-container-low p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Arquivo</div>
            <h3 className="mt-2 text-[13px] font-semibold text-on-surface">Selecionar `.json`</h3>
            <p className="mt-2 text-[11px] leading-6 text-on-surface-variant">
              Use esta opção quando o formato já estiver salvo em arquivo.
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-4 rounded-2xl bg-primary px-4 py-3 text-[11px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]"
            >
              Escolher arquivo JSON
            </button>
          </section>

          <section className="rounded-[24px] border border-surface-container-high bg-surface-container-low p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Conteúdo</div>
            <h3 className="mt-2 text-[13px] font-semibold text-on-surface">Colar JSON</h3>
            <p className="mt-2 text-[11px] leading-6 text-on-surface-variant">
              Cole aqui o objeto completo do formato para carregar sem precisar salvar um arquivo antes.
            </p>
            <textarea
              value={importJsonText}
              onChange={(e) => setImportJsonText(e.target.value)}
              rows={12}
              placeholder={`{\n  "name": "Meu formato",\n  "paperWidthMm": 180,\n  "columns": 3\n}`}
              className="mt-4 w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 font-mono text-[10px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <button
              type="button"
              onClick={handleImportFromText}
              className="mt-4 rounded-2xl border border-primary/20 bg-primary-container/60 px-4 py-3 text-[11px] font-semibold text-on-primary-container"
            >
              Carregar JSON colado
            </button>
          </section>
        </div>
      </Modal>
    </div>
  );
}